import React, { useEffect } from 'react';
import { Formik, FormikProps } from 'formik';
import { Form, Input } from 'formik-antd';
import { Row, Col, Divider } from 'antd';
import * as Yup from 'yup';

import { ExtraItemEnum } from 'enums';
import FormikEffect from 'components/formik/FormikEffect';
import FormItem from 'components/formik/FormItem';
import Checkbox from 'components/formik/Checkbox';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import ContactSection from '../ContactSection';
import ContactsSection from 'components/modals/sections/ContactsSection';
import {
  PreferredContactMethodEnum,
  PrimaryContactForEnum,
} from 'api/backofficeApiClient';
import {
  getCorporateTAPrimaryContacts,
  getCorporateTASupervisors,
  getCorporateTATimesheetApprovers,
} from 'redux/reducers/accountSlice';
import { getContactLabel } from 'utils/assignmentUtils';
import {
  isPrimaryContactsLoadingSelector,
  isTimesheetApproversLoadingSelector,
  primaryContactsSelector,
  supervisorSelector,
  timesheetApproversSelector,
  isSupervisorsLoadingSelector,
} from 'redux/selectors/accountSelector';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

type Props = {
  formRef: any;
  onChange: (formik: FormikProps<ContactInfoFormValues>) => void;
  initialValues?: ContactInfoFormValues;
  memberId?: string;
  companyId?: string;
};

interface Contact {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
}

interface ContactWithTitle extends Contact {
  title: string;
}

interface PrimaryContact extends ContactWithTitle {
  preferredContactMethod?: PreferredContactMethodEnum[];
  contactFor?: PrimaryContactForEnum[];
}

export interface ContactInfoFormValues {
  primaryContacts: {
    bestContacts: string[];
    newPrimaryContact?: PrimaryContact;
  };
  supervisorInfo: {
    supervisor: string;
    newSupervisor?: ContactWithTitle;
  };
  timesheetApprover: {
    sameAsSupervisor: boolean;
    approver: string;
    newTimesheetApprover?: Contact;
    notApplicable: boolean;
  };
  timeBackupApprover: Contact;
}

export const isPrimaryContactOtherSelected = (bestContacts: string[]) =>
  bestContacts.includes(ExtraItemEnum.Other);

export const isOtherOptionSelected = (option: string) =>
  option === ExtraItemEnum.Other;

const contactSchema = {
  firstName: Yup.string()
    .trim()
    .max(120, 'First name should be 120 characters maximum'),
  lastName: Yup.string()
    .trim()
    .max(120, 'Last name should be 120 characters maximum'),
  email: Yup.string()
    .email('Email format is invalid')
    .max(120, 'Email should be 120 characters maximum'),
  phone: Yup.string()
    .trim()
    .max(120, 'Phone number should be 120 characters maximum'),
};

const contactWithTitleSchema = {
  ...contactSchema,
  title: Yup.string().trim().max(120, 'Title should be 120 characters maximum'),
};

const primaryContactSchema = {
  ...contactWithTitleSchema,
  preferredContactMethod: Yup.array().of(Yup.number()),
  contactFor: Yup.array().of(Yup.number()),
};

const initialValidationSchema = {
  primaryContacts: {
    bestContacts: Yup.array().of(Yup.string()),
    newPrimaryContact: Yup.object({}).when('bestContacts', {
      is: isPrimaryContactOtherSelected,
      then: Yup.object(primaryContactSchema),
    }),
  },
  supervisorInfo: {
    supervisor: Yup.string(),

    newSupervisor: Yup.object().when('supervisor', {
      is: isOtherOptionSelected,
      then: Yup.object(contactWithTitleSchema),
    }),
  },
  timesheetApprover: {
    sameAsSupervisor: Yup.boolean(),
    approver: Yup.string(),

    newTimesheetApprover: Yup.object().when('approver', {
      is: isOtherOptionSelected,
      then: Yup.object(contactSchema),
    }),
    notApplicable: Yup.boolean(),
  },
  timeBackupApprover: contactSchema,
};

const submitValidationSchema = Yup.object({
  primaryContacts: Yup.object({
    ...initialValidationSchema.primaryContacts,

    bestContacts: initialValidationSchema.primaryContacts.bestContacts.min(
      1,
      'Field is required',
    ),
    newPrimaryContact: Yup.object({}).when('bestContacts', {
      is: isPrimaryContactOtherSelected,
      then: Yup.object({
        ...primaryContactSchema,
        firstName:
          primaryContactSchema.firstName.required('Field is required'),
        lastName: primaryContactSchema.lastName.required('Field is required'),
        email: primaryContactSchema.email.required('Field is required'),
        phone: primaryContactSchema.phone.required('Field is required'),
        title: primaryContactSchema.title.required('Field is required'),
      }),
    }),
  }),
  supervisorInfo: Yup.object({
    supervisor:
      initialValidationSchema.supervisorInfo.supervisor.required(
        'Field is required',
      ),

    newSupervisor: Yup.object().when('supervisor', {
      is: isOtherOptionSelected,
      then: Yup.object({
        ...contactWithTitleSchema,
        firstName:
          contactWithTitleSchema.firstName.required('Field is required'),
        lastName: contactWithTitleSchema.lastName.required('Field is required'),
        email: contactWithTitleSchema.email.required('Field is required'),
        phone: contactWithTitleSchema.phone.required('Field is required'),
        title: contactWithTitleSchema.title.required('Field is required'),
      }),
    }),
  }),
  timesheetApprover: Yup.object({
    ...initialValidationSchema.timesheetApprover,
    approver: Yup.string().when(['sameAsSupervisor', 'notApplicable'], {
      is: (sameAsSupervisor: boolean, notApplicable: boolean) =>
        !sameAsSupervisor && !notApplicable,
      then: Yup.string().required('Field is required'),
    }),

    newTimesheetApprover: Yup.object().when('approver', {
      is: isOtherOptionSelected,
      then: Yup.object({
        ...contactSchema,
        firstName: contactSchema.firstName.required('Field is required'),
        lastName: contactSchema.lastName.required('Field is required'),
        email: contactSchema.email.required('Field is required'),
        phone: contactSchema.phone.required('Field is required'),
      }),
    }),
  }),
  timeBackupApprover: Yup.object(initialValidationSchema.timeBackupApprover),
});

export default React.memo((props: Props) => {
  const {
    formRef,
    onChange,
    initialValues: predefinedInitialValues,
    companyId,
  } = props;
  const dispatch = useAppDispatch();
  const isAdminRole = useAppSelector(isAdminSelector);
  const primaryContacts = useAppSelector(primaryContactsSelector);
  const isPrimaryContactsLoading = useAppSelector(
    isPrimaryContactsLoadingSelector,
  );
  const supervisors = useAppSelector(supervisorSelector);
  const isSupervisorsLoading = useAppSelector(isSupervisorsLoadingSelector);
  const timesheetApprovers = useAppSelector(timesheetApproversSelector);
  const isTimesheetApproversLoading = useAppSelector(
    isTimesheetApproversLoadingSelector,
  );

  const initialValues: ContactInfoFormValues = {
    primaryContacts: {
      bestContacts: [],
      newPrimaryContact: {
        firstName: '',
        lastName: '',
        email: '',
        title: '',
        phone: '',
        preferredContactMethod: [],
        contactFor: [],
      },
    },
    supervisorInfo: {
      supervisor: '',
      newSupervisor: {
        firstName: '',
        lastName: '',
        email: '',
        title: '',
        phone: '',
      },
    },
    timesheetApprover: {
      sameAsSupervisor: false,
      approver: '',
      newTimesheetApprover: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      },
      notApplicable: false,
    },
    timeBackupApprover: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
  };

  useEffect(() => {
    loadPrimaryContacts();
    loadSupervisors();
    loadTimesheetApprovers();
    // eslint-disable-next-line
  }, []);

  const loadPrimaryContacts = () => {
    dispatch(
      getCorporateTAPrimaryContacts({
        isAdmin: isAdminRole,
        companyId,
      }),
    );
  };

  const loadSupervisors = () => {
    dispatch(
      getCorporateTASupervisors({
        isAdmin: isAdminRole,
        companyId,
      }),
    );
  };

  const loadTimesheetApprovers = () => {
    dispatch(
      getCorporateTATimesheetApprovers({
        isAdmin: isAdminRole,
        companyId,
      }),
    );
  };

  const handleBestContactsDeselect = (
    deselectedValue: string,
    formik: FormikProps<ContactInfoFormValues>,
  ) => {
    if (deselectedValue !== ExtraItemEnum.Other) return;

    formik.setFieldValue(
      'primaryContacts.newPrimaryContact',
      initialValues.primaryContacts.newPrimaryContact,
      false,
    );
  };

  const handleSupervisorChange = (
    value: string,
    formik: FormikProps<ContactInfoFormValues>,
  ) => {
    if (value !== ExtraItemEnum.Other) return;

    formik.setFieldValue(
      'supervisorInfo.newSupervisor',
      initialValues.supervisorInfo.newSupervisor,
      false,
    );
  };

  const handleTimesheetApproverChange = (
    value: string,
    formik: FormikProps<ContactInfoFormValues>,
  ) => {
    if (value !== ExtraItemEnum.Other) return;

    formik.setFieldValue(
      'timesheetApprover.newTimesheetApprover',
      initialValues.timesheetApprover.newTimesheetApprover,
      false,
    );
  };

  const handleTimeSheetApproverSameAsSupervisorChange = (
    e: any,
    formik: FormikProps<ContactInfoFormValues>,
  ) => {
    if (e.target.checked) {
      formik.setFieldValue('timesheetApprover.approver', '', false);

      setTimeout(() => {
        formik.setFieldTouched(
          'timesheetApprover.newTimesheetApprover',
          false,
          true,
        );
      }, 0);

      return;
    }

    formik.setFieldValue(
      'timesheetApprover.newTimesheetApprover',
      initialValues.timesheetApprover.newTimesheetApprover,
      false,
    );
  };

  const handleTimeSheetApproverNotApplicableChange = (
    e: any,
    formik: FormikProps<ContactInfoFormValues>,
  ) => {
    if (!e.target.checked) return;

    formik.setFieldValue('timesheetApprover.approver', '', false);
    formik.setFieldValue('timesheetApprover.sameAsSupervisor', false, false);
    formik.setFieldValue(
      'timesheetApprover.newTimesheetApprover',
      initialValues.timesheetApprover.newTimesheetApprover,
      false,
    );
    setTimeout(() => {
      formik.setFieldTouched(
        'timesheetApprover.newTimesheetApprover',
        false,
        true,
      );
    }, 0);

    return;
  };

  return (
    <Formik
      innerRef={formRef}
      enableReinitialize
      initialValues={predefinedInitialValues || initialValues}
      validationSchema={submitValidationSchema}
      onSubmit={() => {}}
    >
      {(formik) => (
        <>
          <FormikEffect onChange={onChange} />

          <Form layout="vertical">
            <div className="modal-scroll-wrapper">
              <div className="modal-title">Primary contact</div>

              <Row gutter={40} align="middle">
                <Col xs={24} md={12}>
                  <FormItem
                    name="primaryContacts.bestContacts"
                    label="Primary contact *"
                  >
                    <FormikSelectWithLoading
                      isLoading={isPrimaryContactsLoading}
                      mode="multiple"
                      name="primaryContacts.bestContacts"
                      placeholder="Select primary contact"
                      onDeselect={(value: string) =>
                        handleBestContactsDeselect(value, formik)
                      }
                    >
                      {!isPrimaryContactsLoading && (
                        <>
                          {primaryContacts.map((primaryContact) => (
                            <FormikSelectWithLoading.Option
                              key={`select-primary-contact-${primaryContact.contactId}`}
                              value={primaryContact.contactId || ''}
                            >
                              {getContactLabel(primaryContact)}
                            </FormikSelectWithLoading.Option>
                          ))}
                          <FormikSelectWithLoading.Option
                            key="select-primary-other-contact"
                            value={ExtraItemEnum.Other}
                          >
                            {ExtraItemEnum.Other}
                          </FormikSelectWithLoading.Option>
                        </>
                      )}
                    </FormikSelectWithLoading>
                  </FormItem>
                </Col>

                {isPrimaryContactOtherSelected(
                  formik.values.primaryContacts.bestContacts,
                ) && (
                  <Col xs={24} md={12}>
                    <FormItem
                      label="Title *"
                      name="primaryContacts.newPrimaryContact.title"
                      data-id="primaryContacts-newPrimaryContact-title"
                    >
                      <Input
                        placeholder="Enter title"
                        name="primaryContacts.newPrimaryContact.title"
                        autoComplete="off"
                      />
                    </FormItem>
                  </Col>
                )}
              </Row>

              {isPrimaryContactOtherSelected(
                formik.values.primaryContacts.bestContacts,
              ) && (
                <ContactsSection
                  formNamePrefix="primaryContacts.newPrimaryContact"
                  isTitleEnabled={false}
                  showPreferredContactMethod
                  showContactFor
                  preferredContactMethodRequired={false}
                  contactForRequired={false}
                />
              )}

              <Divider className="modal-divider after-form" />

              <div className="modal-title">Supervisor information</div>

              <Row gutter={40} align="middle">
                <Col xs={24} md={12}>
                  <FormItem
                    name="supervisorInfo.supervisor"
                    label="Supervisor *"
                  >
                    <FormikSelectWithLoading
                      isLoading={isSupervisorsLoading}
                      name="supervisorInfo.supervisor"
                      placeholder="Select supervisor"
                      onChange={(value: string) =>
                        handleSupervisorChange(value, formik)
                      }
                    >
                      {!isSupervisorsLoading && (
                        <>
                          {supervisors.map((supervisor) => (
                            <FormikSelectWithLoading.Option
                              key={`select-primary-contact-${supervisor.supervisorId}`}
                              value={supervisor.supervisorId || ''}
                            >
                              {getContactLabel(supervisor)}
                            </FormikSelectWithLoading.Option>
                          ))}
                          <FormikSelectWithLoading.Option
                            key="select-supervisor-other-contact"
                            value={ExtraItemEnum.Other}
                          >
                            {ExtraItemEnum.Other}
                          </FormikSelectWithLoading.Option>
                        </>
                      )}
                    </FormikSelectWithLoading>
                  </FormItem>
                </Col>

                {isOtherOptionSelected(
                  formik.values.supervisorInfo.supervisor,
                ) && (
                  <Col xs={24} md={12}>
                    <FormItem
                      label="Title *"
                      name="supervisorInfo.newSupervisor.title"
                      data-id="primaryContacts-newPrimaryContact-title"
                    >
                      <Input
                        placeholder="Enter title"
                        name="supervisorInfo.newSupervisor.title"
                      />
                    </FormItem>
                  </Col>
                )}

                {isOtherOptionSelected(
                  formik.values.supervisorInfo.supervisor,
                ) && (
                  <>
                    <ContactSection formNamePrefix="supervisorInfo.newSupervisor" />
                  </>
                )}
              </Row>

              <Divider className="modal-divider after-form" />

              <div className="modal-title">Timesheet approver information</div>

              <Row gutter={40} align="middle">
                <Col xs={24} md={12}>
                  <FormItem
                    name="timesheetApprover.approver"
                    label="Timesheet approver *"
                  >
                    <FormikSelectWithLoading
                      isLoading={isTimesheetApproversLoading}
                      name="timesheetApprover.approver"
                      placeholder="Select timesheet approver"
                      onChange={(value: string) =>
                        handleTimesheetApproverChange(value, formik)
                      }
                      disabled={
                        formik.values.timesheetApprover.sameAsSupervisor ||
                        formik.values.timesheetApprover.notApplicable
                      }
                    >
                      {!isTimesheetApproversLoading && (
                        <>
                          {timesheetApprovers.map((approver) => (
                            <FormikSelectWithLoading.Option
                              key={`select-timesheet-approver-${approver.timesheetApproverId}`}
                              value={approver.timesheetApproverId || ''}
                            >
                              {getContactLabel(approver)}
                            </FormikSelectWithLoading.Option>
                          ))}
                          <FormikSelectWithLoading.Option
                            key="select-timesheet-approver-other-contact"
                            value={ExtraItemEnum.Other}
                          >
                            {ExtraItemEnum.Other}
                          </FormikSelectWithLoading.Option>
                        </>
                      )}
                    </FormikSelectWithLoading>
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    name="timesheetApprover.sameAsSupervisor"
                    className="checkbox-wrapper radio-alignment"
                  >
                    <Checkbox
                      name="timesheetApprover.sameAsSupervisor"
                      onChange={(e) =>
                        handleTimeSheetApproverSameAsSupervisorChange(e, formik)
                      }
                      disabled={formik.values.timesheetApprover.notApplicable}
                    >
                      The same as supervisor
                    </Checkbox>
                  </FormItem>
                </Col>

                {isOtherOptionSelected(
                  formik.values.timesheetApprover.approver,
                ) && (
                  <>
                    <ContactSection
                      formNamePrefix="timesheetApprover.newTimesheetApprover"
                      disabled={formik.values.timesheetApprover.notApplicable}
                    />
                  </>
                )}

                <Col xs={24}>
                  <FormItem
                    name="timesheetApprover.notApplicable"
                    className="checkbox-wrapper"
                  >
                    <Checkbox
                      name="timesheetApprover.notApplicable"
                      onChange={(e) =>
                        handleTimeSheetApproverNotApplicableChange(e, formik)
                      }
                    >
                      Not applicable
                    </Checkbox>
                  </FormItem>
                </Col>
              </Row>

              <Divider className="modal-divider after-form" />

              <div className="modal-title">
                Timesheet backup approver information
              </div>

              <Row gutter={40} align="middle">
                <ContactSection
                  formNamePrefix="timeBackupApprover"
                  isRequired={false}
                />
              </Row>
            </div>
          </Form>
        </>
      )}
    </Formik>
  );
});
